/**
 * Watch Zone Overlay Component
 *
 * Displays user's watch zones on the map as circles or polygons.
 * Each zone can be toggled on/off for visibility.
 */

import { useMemo } from 'react';
import { Circle, Polygon, Popup } from 'react-leaflet';
import { WatchZone } from '../api/client';

interface WatchZoneOverlayProps {
  zones: WatchZone[];
  visibleZoneIds: Set<string>;
  onZoneClick?: (zone: WatchZone) => void;
}

// Zone color based on enabled status. The className 'watch-zone-shape' is
// styled in App.css with `pointer-events: stroke` so taps that land inside
// the zone's fill area (e.g. on an aircraft or fire marker that happens to
// be inside the zone) fall through to the underlying marker. Only the
// border itself remains clickable, which still gives users a way to open
// the zone's popup or trigger onZoneClick.
const ZONE_COLORS = {
  enabled: {
    color: '#22c55e',
    fillColor: '#22c55e',
    fillOpacity: 0.1,
    weight: 2,
    className: 'watch-zone-shape',
  },
  disabled: {
    color: '#6b7280',
    fillColor: '#6b7280',
    fillOpacity: 0.05,
    weight: 1,
    dashArray: '5, 5',
    className: 'watch-zone-shape',
  },
};

export default function WatchZoneOverlay({
  zones,
  visibleZoneIds,
  onZoneClick,
}: WatchZoneOverlayProps) {
  // Filter to only visible zones
  const visibleZones = useMemo(() => {
    return zones.filter(zone => visibleZoneIds.has(zone.zoneId));
  }, [zones, visibleZoneIds]);

  return (
    <>
      {visibleZones.map((zone) => {
        const style = zone.isEnabled ? ZONE_COLORS.enabled : ZONE_COLORS.disabled;

        if (zone.geometry.type === 'circle' && zone.geometry.center) {
          return (
            <Circle
              key={zone.zoneId}
              center={[zone.geometry.center.lat, zone.geometry.center.lon]}
              radius={(zone.geometry.radiusKm || 25) * 1000}
              pathOptions={style}
              eventHandlers={{
                click: () => onZoneClick?.(zone),
              }}
            >
              <Popup className="watch-zone-popup-wrapper">
                <div className="watch-zone-popup">
                  <div className="watch-zone-popup-header">
                    <span className={`watch-zone-popup-status ${zone.isEnabled ? 'enabled' : 'disabled'}`} />
                    <strong>{zone.name}</strong>
                  </div>
                  <div className="watch-zone-popup-info">
                    <span>Radius: {zone.geometry.radiusKm} km</span>
                  </div>
                  <div className="watch-zone-popup-types">
                    {zone.preferences.incidentTypes.map(type => (
                      <span key={type} className="watch-zone-popup-type">
                        {type === 'FIRE_INCIDENT' && '🔥'}
                        {type === 'HOTSPOT' && '🛰️'}
                        {type === 'ROAD_CLOSURE' && '🚧'}
                        {type === 'POWER_OUTAGE' && '⚡'}
                      </span>
                    ))}
                  </div>
                </div>
              </Popup>
            </Circle>
          );
        }

        if (zone.geometry.type === 'polygon' && zone.geometry.coordinates) {
          const positions = zone.geometry.coordinates.map(c => [c.lat, c.lon] as [number, number]);
          return (
            <Polygon
              key={zone.zoneId}
              positions={positions}
              pathOptions={style}
              eventHandlers={{
                click: () => onZoneClick?.(zone),
              }}
            >
              <Popup className="watch-zone-popup-wrapper">
                <div className="watch-zone-popup">
                  <div className="watch-zone-popup-header">
                    <span className={`watch-zone-popup-status ${zone.isEnabled ? 'enabled' : 'disabled'}`} />
                    <strong>{zone.name}</strong>
                  </div>
                  <div className="watch-zone-popup-info">
                    <span>Custom polygon area</span>
                  </div>
                  <div className="watch-zone-popup-types">
                    {zone.preferences.incidentTypes.map(type => (
                      <span key={type} className="watch-zone-popup-type">
                        {type === 'FIRE_INCIDENT' && '🔥'}
                        {type === 'HOTSPOT' && '🛰️'}
                        {type === 'ROAD_CLOSURE' && '🚧'}
                        {type === 'POWER_OUTAGE' && '⚡'}
                      </span>
                    ))}
                  </div>
                </div>
              </Popup>
            </Polygon>
          );
        }

        return null;
      })}
    </>
  );
}
